import styles from './ProcessDS.module.scss'
import { useRef } from 'react'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

interface ProcessItem {
  number: string
  title: string
  read: string
  text?: JSX.Element
  todos?: string[]
  notesTitle?: string | JSX.Element
  notesText?: string
  imageSrc: string
  imageAlt: string
}

const processData: ProcessItem[] = [
  {
    number: '01',
    title: '調査・ヒアリング〜要件定義',
    read: '現行プロダクトやガイドライン、業務フローを理解し、初期に必要なデザインシステムの要件を定義します',
    text: (
      <>
        調査・ヒアリングでは、関係者へのヒアリングや業務フローの把握を進めます。併せて、現行のプロダクトやブランド資産について理解を深め、コンポーネントの棚卸しを行います。
        <br />
        デザインシステム要件定義では、関係者へのヒアリングを元に、現行プロダクトや業務フローへの理解を深めます。それらの情報を基に本デザインシステムで定義する要件範囲を検討します。
      </>
    ),
    todos: [
      'コンポーネント',
      'カラーモード(ライト/ダークモード)',
      'ユーザー(アクター)の理解、整理',
      'アクセシビリティ要件',
      'ガイドラインの内容・展開方法',
      '拡張性の考慮',
    ],
    imageSrc: '/img/services/designsystem_process01.webp',
    imageAlt: '調査・ヒアリング〜要件定義',
  },
  {
    number: '02',
    title: 'トーン&マナー設計・アピアランス検証',
    read: 'プロダクトやブランドの方向性を支える基本的なルールや理念を策定します',
    text: (
      <>
        ブランド・プロダクトの特性を分析し、ふさわしいブランド人格を検討します。ブランドアーキタイプ分析の手法を用いることで、感覚的になりがちな検討をより論理的に進め、大幅なすれ違いを防ぐことができます。
        <br />
        アピアランス憲章では、デザインシステムを現場に浸透させ、効果を発揮させるべく、実際にプロダクトを利用する方を対象に調査を実施します。制作前/中にヒアリングを行い、意見を取り入れることで、デザインシステムとして適切な方針を見定めます。
      </>
    ),
    todos: ['性格・人格設定', 'ムードボード', 'サンプルデザイン', 'ビジュアルゴール設定', 'デザイン原則策定'],
    imageSrc: '/img/services/designsystem_process02.webp',
    imageAlt: 'トーン&マナー設計・アピアランス検証',
  },
  {
    number: '03',
    title: 'デザイン基盤構築',
    read: 'プロダクト全体で一貫性と拡張性を両立するため、設計ルールを体系化します',
    text: (
      <>
        トークン設計は、色、余白、フォントなどの基本要素を体系化し、一貫性を保ちながら効率的なデザイン運用を実現します。デザインとエンジニアリングの共通言語としても機能し、変更の影響を可視化してスムーズな開発を支援します。
        <br />
        設計・実装の効率化と品質向上を目指し、デザインに必要なルールを整理します。用語やアクセシビリティ、モーションなどに関するルールを整理し、全体の統一感を実現します。
      </>
    ),
    todos: ['トークン設計', 'デザインルールの整理', '命名規則'],
    imageSrc: '/img/services/designsystem_process03.webp',
    imageAlt: 'デザイン基盤構築',
  },
  {
    number: '04',
    title: 'コンポーネント設計',
    read: 'デザイン基盤を元に、Variablesを適用したFigmaコンポーネントを作成します',
    text: (
      <>
        Variablesをコンポーネントに適用し、状態ごとのバリアンツ作成、テーマ切り替えの検証などを行います。
        <br />
        加えてFigmaデザインデータを開発とスムーズに連携させるため、Figma CSS
        PluginやCodeConnectなどのツール活用を前提にコンポーネントを設計・構築します。デザインとコードのギャップを最小限に抑え、開発プロセスの効率化を目指し、実装したUIコンポーネントが意図したものになっているかをUIベースで確認をし、フィードバックをします。
      </>
    ),
    todos: ['Figmaコンポーネントの作成', 'デザイン・開発プロセスの効率化', 'UIテスト'],
    imageSrc: '/img/services/designsystem_process04.webp',
    imageAlt: 'コンポーネント設計',
  },
  {
    number: '05',
    title: 'Reactコンポーネントライブラリ開発',
    read: 'FigmaデザインをReactコンポーネントライブラリで表現します。プラグインや連携ツールを積極的に活用することで効率よく高品質な開発ワークフローの実現を目指します',
    text: (
      <>
        開発ツール利用例
        <br />
        開発技術：Node.js, React, TypeScript, shadcn/ui(Radix), React Aria
        <br />
        UIレビュー／テスト：Storybook, Chromatic
        <br />
        その他： GitHub, GitHub Actions, VS Code, Figma for VS Code, Figma Code Connect など
      </>
    ),
    imageSrc: '/img/services/designsystem_process05.webp',
    imageAlt: 'Reactコンポーネントライブラリ開発',
  },
  {
    number: '06',
    title: 'ガイドライン作成',
    read: 'コンポーネントやパターンの用途やデザインルールをまとめ、新たなデザインパターンを制作する上でのガイドラインを作ります',
    todos: ['デザイン基盤ガイドライン', 'コンポーネントガイドライン'],
    notesTitle: (
      <>
        <span>NOTES</span>AIリーダブルなドキュメント化
      </>
    ),
    notesText:
      '最新のAIツールがドキュメントを理解し活用できるよう、構造化された形式でドキュメントを作成することで、様々なメリットが生まれます。AI支援ツールによるコード生成やデザイン提案の精度が向上するだけでなく、ドキュメント自体の検索性と再利用性も高まります。また、新規参画メンバーの学習効率改善にも寄与し、将来的なAIとの協業を見据えた設計となるため、長期的な運用においても価値を発揮します。i3DESIGNでは、このような次世代のデザインシステム運用を考慮した設計を提案しています。',
    imageSrc: '/img/services/designsystem_process06.webp',
    imageAlt: 'ガイドライン作成',
  },
  {
    number: '07',
    title: '運用',
    read: 'デザインシステムは作って終わりではなく、継続的に育てていくことが重要です。そのためには、効果的な運用と改善を支える仕組みを整備し、組織へデザインシステムを浸透させていくことが大切です',
    text: (
      <>
        変更履歴を明確にし、バージョン管理とデザイン要素の承認プロセスを確立します。Figmaや
        Githubのバージョン管理機能を活用します。
        <br />
        運用中の課題やフィードバックを効率的に収集し、改善に向けたプロセスを策定します。バージョン管理や承認プロセスも含めた課題管理の仕組みを整え、デザインシステムの品質向上を目指します。
        <br />
        組織全体にデザインシステムを浸透させるため、勉強会やレクチャー会を実施し、システムの活用方法や意図を理解できるようサポートします。
      </>
    ),
    todos: ['バージョン管理', '承認プロセスの策定', '課題改修の仕組み作り', 'レクチャー会'],
    imageSrc: '/img/services/designsystem_process07.webp',
    imageAlt: '運用',
  },
]

const ProcessDS: React.FC = () => {
  const animeRef = useRef<HTMLDivElement>(null)
  const isVisible = useElementVisible(animeRef, 0.1)

  return (
    <section
      className={`${styles.wrapper} ${animationStyles.fade} ${isVisible ? animationStyles.fadeOn : ''}`}
      ref={animeRef}
    >
      <h2 className={styles.sectionTitle}>サービス提供プロセス</h2>
      <div className={styles.processWrapper}>
        {processData.map((item, index) => (
          <article key={index} className={styles.process}>
            <div className={styles.process__number}>{item.number}</div>
            <div className={styles.process__cts}>
              <div className={styles.item}>
                <h3 className={styles.item__title}>{item.title}</h3>
                <p className={styles.item__read}>{item.read}</p>
                {item.text && <p className={styles.item__text}>{item.text}</p>}
                {item.todos && (
                  <dl className={styles.item__list}>
                    <dt className={styles.item__list__title}>このフェーズで行うこと</dt>
                    {item.todos.map((todo, todoIndex) => (
                      <dd key={todoIndex} className={styles.item__list__todo}>
                        {todo}
                      </dd>
                    ))}
                  </dl>
                )}
                {item.notesTitle && item.notesText && (
                  <dl className={styles.item__notes}>
                    <dt className={styles.item__notes__title}>{item.notesTitle}</dt>
                    <dd className={styles.item__notes__text}>{item.notesText}</dd>
                  </dl>
                )}
              </div>
              <figure className={styles.process__cts__image}>
                <img src={item.imageSrc} alt={item.imageAlt} />
              </figure>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

export default ProcessDS
